import React, { useEffect, useRef, useState } from 'react';
import styled, { keyframes, css } from 'styled-components';
import TimerDisplay from './TimerDisplay';
import { useTranslation } from '../../hooks/useTranslation';

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const slideUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const confetti = keyframes`
  0% { transform: translateY(-100vh) rotate(0deg); opacity: 1; }
  100% { transform: translateY(100vh) rotate(720deg); opacity: 0; }
`;

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 9999;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  animation: ${fadeIn} 0.5s ease-out;

  ${props => props.$theme === 'dark' && css`
    background: linear-gradient(135deg, #0a0a0a 0%, #1a1a2e 50%, #0a0a0a 100%);
  `}

  ${props => props.$theme === 'light' && css`
    background: linear-gradient(135deg, #f0f0f0 0%, #ffffff 50%, #f0f0f0 100%);
    color: #1a1a1a;
  `}

  ${props => props.$theme === 'brand' && css`
    background: linear-gradient(135deg, #0099ff 0%, #9933ff 50%, #ff0066 100%);
  `}
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 32px;
  text-align: center;
  padding: 40px;
  animation: ${slideUp} 0.6s ease-out 0.2s both;
`;

const Icon = styled.div`
  font-size: 80px;
  line-height: 1;
  margin-bottom: 16px;
`;

const Title = styled.h1`
  font-size: 64px;
  font-weight: 700;
  margin: 0;
  color: ${props => props.$theme === 'light' ? '#1a1a1a' : '#ffffff'};
  text-shadow: ${props => props.$theme === 'brand' ? '0 2px 20px rgba(0,0,0,0.3)' : 'none'};
`;

const Subtitle = styled.p`
  font-size: 28px;
  color: ${props => props.$theme === 'light' ? '#666666' : 'rgba(255, 255, 255, 0.7)'};
  margin: 0;
  max-width: 600px;
`;

const TimerWrapper = styled.div`
  margin-top: 24px;
`;

const ReturnTime = styled.div`
  font-size: 20px;
  color: ${props => props.$theme === 'light' ? '#888888' : 'rgba(255, 255, 255, 0.5)'};
  margin-top: 16px;
`;

const ConfettiPiece = styled.div`
  position: absolute;
  width: 10px;
  height: 10px;
  background: ${props => props.$color};
  top: -10px;
  left: ${props => props.$left}%;
  animation: ${confetti} ${props => props.$duration}s linear infinite;
  animation-delay: ${props => props.$delay}s;
  opacity: 0;
`;

const CONFETTI_COLORS = ['#0099ff', '#ffcc00', '#ff0066', '#00ff88', '#9933ff'];

const TYPE_CONFIG = {
  countdown: {
    icon: null,
    defaultTitle: 'Seuraava vaihe alkaa',
  },
  break: {
    icon: '☕',
    defaultTitle: 'Tauko',
  },
  message: {
    icon: '📢',
    defaultTitle: '',
  },
  transition: {
    icon: '▶',
    defaultTitle: 'Seuraavaksi',
  },
  celebration: {
    icon: '🎉',
    defaultTitle: 'Onneksi olkoon!',
    showConfetti: true,
  },
};

/**
 * Calculate return time string based on remaining seconds
 */
const calculateReturnTime = (remainingSeconds, language = 'fi') => {
  const now = new Date();
  const returnDate = new Date(now.getTime() + remainingSeconds * 1000);
  const dateLocale = language === 'en' ? 'en-GB' : language === 'sv' ? 'sv-SE' : 'fi-FI';
  return returnDate.toLocaleTimeString(dateLocale, {
    hour: '2-digit',
    minute: '2-digit',
  });
};

/**
 * InterludeOverlay - Full-screen takeover for breaks, transitions, and messages
 *
 * @param {'countdown' | 'break' | 'message' | 'transition' | 'celebration'} type - Interlude type
 * @param {string} title - Main title text
 * @param {string} subtitle - Secondary text
 * @param {number} duration - Total duration in seconds (null for manual dismiss)
 * @param {number} remaining - Current remaining seconds
 * @param {boolean} showProgress - Show progress bar in timer
 * @param {boolean} showReturnTime - Show "Palataan klo X:XX" text
 * @param {'dark' | 'light' | 'brand'} theme - Visual theme
 * @param {boolean} isPaused - Whether timer is paused
 * @param {function} onComplete - Callback when timer reaches zero
 */
const InterludeOverlay = ({
  type = 'countdown',
  title,
  subtitle,
  duration,
  remaining,
  showProgress = true,
  showReturnTime = false,
  theme = 'dark',
  isPaused = false,
  onComplete,
}) => {
  const { language } = useTranslation();
  const config = TYPE_CONFIG[type] || TYPE_CONFIG.countdown;
  const displayTitle = title || config.defaultTitle;
  const hasTimer = duration !== null && duration !== undefined && duration > 0;

  // Track completion
  const completedRef = useRef(false);
  useEffect(() => {
    if (hasTimer && remaining === 0 && !completedRef.current) {
      completedRef.current = true;
      onComplete?.();
    }
  }, [remaining, hasTimer, onComplete]);

  // Reset completion tracking when duration changes
  useEffect(() => {
    completedRef.current = false;
  }, [duration]);

  // Generate confetti pieces for celebration
  const confettiPieces = config.showConfetti
    ? Array.from({ length: 50 }, (_, i) => ({
        id: i,
        color: CONFETTI_COLORS[i % CONFETTI_COLORS.length],
        left: Math.random() * 100,
        delay: Math.random() * 5,
        duration: 3 + Math.random() * 2,
      }))
    : [];

  return (
    <Overlay $theme={theme}>
      {config.showConfetti && confettiPieces.map((piece) => (
        <ConfettiPiece
          key={piece.id}
          $color={piece.color}
          $left={piece.left}
          $delay={piece.delay}
          $duration={piece.duration}
        />
      ))}

      <Content>
        {config.icon && <Icon>{config.icon}</Icon>}

        {displayTitle && <Title $theme={theme}>{displayTitle}</Title>}

        {subtitle && <Subtitle $theme={theme}>{subtitle}</Subtitle>}

        {hasTimer && (
          <TimerWrapper>
            <TimerDisplay
              remaining={remaining}
              total={duration}
              size="fullscreen"
              showProgress={showProgress}
              isPaused={isPaused}
            />
          </TimerWrapper>
        )}

        {showReturnTime && hasTimer && remaining > 0 && (
          <ReturnTime $theme={theme}>
            Palataan klo {calculateReturnTime(remaining, language)}
          </ReturnTime>
        )}
      </Content>
    </Overlay>
  );
};

export default InterludeOverlay;
